import { useMemo } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import {
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Tooltip,
  colors,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { Button, LoadingSkeleton } from 'components'
import { minuteOptions, showError } from 'utils'
import { updatePartner } from 'api/partners'
import { PlatformHeader } from './styles'

type Props = {
  partner: any
}

const PrepTimeShortcut = ({ partner }: Props) => {

  const platformAttributes = partner?.attributes

  // Filter out 'N/A' and "0" options from minuteOptions (keep them for other platforms prep times)
  const filteredMinuteOptions = minuteOptions.filter(option => 
    option.value !== 'N/A' && option.value !== '0'
  )

  // Memoize initial form values based on the partner attributes
  const initialValues = useMemo(
    () => ({
      pickup_time_estimate: platformAttributes?.pickup_time_estimate ?? '',
      delivery_time_estimate: platformAttributes?.delivery_time_estimate ?? '',
    }),
    [platformAttributes],
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      try {
        // Update partner prep times via API call
        await updatePartner(partner.id, {
          attributes: {
            pickup_time_estimate: values.pickup_time_estimate || null,
            delivery_time_estimate: values.delivery_time_estimate || null,
            upcharge: partner.attributes?.upcharge ?? 0,
          },
        })
        toast.success('Prep times updated!', { autoClose: 5000 })
      } catch (err) {
        showError(err)
      }
    },
  })
  
  const isLoading = !partner || !partner.attributes

  return (
    <Box mt={3}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <Box display='flex' alignItems='center' gap={1} mb={2}>
            <PlatformHeader>Partner Prep Times</PlatformHeader>
            <Tooltip title="These are the default prep times for the location. To update the individual Order Platform prep times click on the Order Platform you'd like to update on the Menu Management page.">
              <InfoIcon sx={{ color: colors.blue[900], fontSize: '1rem', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <form onSubmit={formik.handleSubmit}>
            <Box display='flex' gap={2} flexWrap='wrap'>
              <FormControl variant='outlined' sx={{ minWidth: 150 }}>
                <InputLabel id='pickup-time-label'>Pickup Prep Time</InputLabel>
                <Select
                  labelId='pickup-time-label'
                  name='pickup_time_estimate'
                  data-testid='pickup-prep-select'
                  value={formik.values.pickup_time_estimate || ''}
                  onChange={(e) =>
                    formik.setFieldValue(
                      'pickup_time_estimate',
                      e.target.value ? Number(e.target.value) : '',
                    )
                  }
                  label='Pickup Prep Time'
                >
                  {filteredMinuteOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant='outlined' sx={{ minWidth: 150 }}>
                <InputLabel id='delivery-time-label'>Delivery Prep Time</InputLabel>
                <Select
                  labelId='delivery-time-label'
                  name='delivery_time_estimate'
                  data-testid='delivery-prep-select'
                  value={formik.values.delivery_time_estimate || ''}
                  onChange={(e) =>
                    formik.setFieldValue(
                      'delivery_time_estimate',
                      e.target.value ? Number(e.target.value) : '',
                    )
                  }
                  label='Delivery Prep Time'
                >
                  {filteredMinuteOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display='flex' alignItems='center'>
                <Button 
                  type='submit'
                  size='small'
                  narrow
                >
                  Save
                </Button>
              </Box>
            </Box>
          </form>
        </>
      )}
    </Box>
  )
}

export default PrepTimeShortcut
